import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Heart, ArrowRight, Users2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { ANIMALS } from "@/lib/animals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const [ok, setOk] = useState(null);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ email: "", username: "", password: "", password2: "", gender: "Wanita", animal: "hamster" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    // Ping the invite (backend has no public status endpoint; try accept-form; assume valid until submit)
    setOk(true);
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim() || !f.username.trim() || !f.password) { toast.error("Lengkapi semua kolom"); return; }
    if (f.password !== f.password2) { toast.error("Konfirmasi password tidak sama"); return; }
    if (f.password.length < 4) { toast.error("Password minimal 4 karakter"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/public/partner/accept/${token}`, {
        email: f.email.trim(), username: f.username.trim(), password: f.password,
        gender: f.gender, animal: f.animal,
      });
      toast.success("Berhasil terhubung! Silakan login.");
      setTimeout(() => nav("/login"), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menerima undangan");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] px-4 py-12 text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=Inter:wght@400;500;600;700&display=swap" />
      <div className="mx-auto max-w-lg">
        <div className="mb-8 text-center">
          <Users2 className="mx-auto h-10 w-10 text-[#e6c98a]" />
          <h1 className="mt-4 text-5xl italic" style={{ fontFamily: "'Great Vibes', cursive", color: "#e6c98a" }}>Terima Undangan Pasangan</h1>
          <p className="mt-2 text-sm text-white/60" style={{ fontFamily: "'Inter', sans-serif" }}>
            Setelah mendaftar, akun Anda akan otomatis terhubung dengan pasangan pengundang & mengikuti paket yang sama.
          </p>
        </div>
        <motion.form onSubmit={submit} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-md">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60">Email</Label>
              <Input data-testid="pa-email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="email@contoh.com" className="mt-2 border-white/15 bg-white/5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60">Username</Label>
              <Input data-testid="pa-username" value={f.username} onChange={(e) => set("username", e.target.value)} placeholder="username unik" className="mt-2 border-white/15 bg-white/5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Password</Label>
                <div className="relative mt-2">
                  <Input data-testid="pa-password" type={show ? "text" : "password"} value={f.password} onChange={(e) => set("password", e.target.value)} className="border-white/15 bg-white/5 pr-9" />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Ulangi Password</Label>
                <Input data-testid="pa-password2" type={show ? "text" : "password"} value={f.password2} onChange={(e) => set("password2", e.target.value)} className="mt-2 border-white/15 bg-white/5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Gender</Label>
                <select data-testid="pa-gender" value={f.gender} onChange={(e) => set("gender", e.target.value)} className="mt-2 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm">
                  <option value="Pria">Pria</option><option value="Wanita">Wanita</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Karakter Hewan</Label>
                <select data-testid="pa-animal" value={f.animal} onChange={(e) => set("animal", e.target.value)} className="mt-2 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm">
                  {ANIMALS.map((a) => <option key={a.key} value={a.key}>{a.emoji} {a.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          <Button data-testid="pa-submit" type="submit" disabled={loading} className="mt-6 w-full rounded-full py-6 text-[#0f0a08]" style={{ background: "linear-gradient(90deg,#c9a35c,#e6c98a)", fontFamily: "'Inter', sans-serif" }}>
            {loading ? "Memproses..." : <span className="flex items-center justify-center gap-2 font-semibold">Terima Undangan <ArrowRight className="h-4 w-4" /></span>}
          </Button>
          <p className="mt-4 text-center text-xs text-white/40" style={{ fontFamily: "'Inter', sans-serif" }}>
            Sudah punya akun? <Link to="/login" className="text-[#e6c98a] underline">Masuk</Link>
          </p>
        </motion.form>
        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-white/40" style={{ fontFamily: "'Inter', sans-serif" }}>
          <Heart className="h-3 w-3 fill-[#c9a35c] text-[#c9a35c]" /> Wedifylab
        </p>
      </div>
    </div>
  );
}
